import type { Campana } from '../../api/ops/campanas';
import type { NombreIcono } from '../../componentes/iconos';
import { cifra, diaMes, diaYHora } from '../../logica/fechasCortas';
import { PAUSA, tituloPausa } from './reglas';

/** Líneas de una campaña en la lista (12.1) y en su ficha (12.3), con los textos del diseño. */
/** Cuándo: línea principal y la de debajo, por estado. */
export function cuandoDe(c: Campana): [string, string] {
    switch (c.status) {
        case 'draft':
            return ['Sin programar', `Creada el ${diaMes(c.created_at)}`];
        case 'scheduled':
            return [diaYHora(c.scheduled_at), 'Programada'];
        case 'sending':
            return [diaYHora(c.sent_at), 'Empezó'];
        case 'paused':
            return [diaYHora(c.sent_at), 'Pausada'];
        case 'sent':
            return [diaYHora(c.sent_at), 'Enviada'];
        case 'cancelled':
            return ['—', `Cancelada el ${diaMes(c.finished_at ?? c.created_at)}`];
        case 'stopped':
            return [diaYHora(c.sent_at), `Detenida · ${diaYHora(c.finished_at)}`];
    }
}

/** La columna Resultados de la lista (12.1). */
export function resultadoDe(c: Campana): string {
    const r = c.results;
    const encargados = c.handed_count ?? 0;
    const total = c.frozen_recipients ?? 0;
    switch (c.status) {
        case 'sent':
            return r?.delivered == null ? `${cifra(r?.recipients ?? total)} enviados · ${cifra(r?.conversions ?? 0)} reservas y compras` : `${cifra(r.delivered)} entregados · ${cifra(r.conversions ?? 0)} reservas y compras`;
        case 'sending':
        case 'paused':
            return `${cifra(encargados)} de ${cifra(total)} encargados`;
        case 'stopped':
            return `Salieron ${cifra(encargados)} de ${cifra(total)}`;
        case 'scheduled':
            return 'Se calcula a la hora de salir';
        case 'cancelled':
            return 'No se envió nada';
        case 'draft':
            return contenidoListo(c) ? 'Lista para revisar y enviar' : 'Falta el mensaje';
    }
}

export function contenidoListo(c: Pick<Campana, 'channel' | 'content'>): boolean {
    const m = c.content;
    if (m === undefined) {
        return false;
    }
    if (c.channel === 'whatsapp') {
        return m.meta_template_id != null && m.meta_template_id !== '';
    }
    if (c.channel === 'telegram') {
        return (m.text ?? '').trim() !== '';
    }
    return (m.email_subject ?? '').trim() !== '' && ((m.email_body_html ?? '').trim() !== '' || (m.email_body_text ?? '').trim() !== '');
}

/** La franja de estado de la ficha (12.3), con los textos del diseño. */
export function franjaDe(c: Campana, quedan: number): { icono: NombreIcono; titulo: string; texto: string } {
    switch (c.status) {
        case 'scheduled':
            return {
                icono: 'calendar-clock',
                titulo: `Sale el ${diaYHora(c.scheduled_at).toLocaleLowerCase('es')}`,
                texto: `Hora del negocio. A esa hora se vuelve a calcular a quién llega${c.last_preview == null ? '' : ` (ahora, unos ${cifra(c.last_preview.audience_size)})`} y se comprueba el saldo. Hasta entonces puedes cambiarla.`,
            };
        case 'sending':
            return { icono: 'send', titulo: 'Enviándose ahora', texto: 'Los mensajes se encargan poco a poco y cada uno se vuelve a comprobar antes de salir. Lo ya encargado no se puede recuperar.' };
        case 'paused': {
            const p = c.pause_reason == null ? null : PAUSA[c.pause_reason];
            return {
                icono: 'pause',
                titulo: tituloPausa(c.pause_reason),
                texto: `${p === null ? '' : `${p.texto} `}Quedan ${cifra(quedan)} por enviar.${p?.porCupo === true ? ' Cuando haya saldo, pulsa «Reanudar»: de momento no sigue sola.' : ''}`,
            };
        }
        case 'sent':
            return { icono: 'circle-check', titulo: `Enviada el ${diaYHora(c.sent_at).toLocaleLowerCase('es')}`, texto: `${cifra(c.frozen_recipients ?? c.handed_count ?? 0)} mensajes encargados${c.finished_at == null ? '' : `; terminó el ${diaYHora(c.finished_at).toLocaleLowerCase('es')}`}. Las reservas y compras se siguen sumando 7 días.` };
        case 'cancelled':
            return { icono: 'calendar-x', titulo: 'Cancelada antes de salir', texto: `Se canceló${c.finished_at == null ? '' : ` el ${diaMes(c.finished_at)}`}. No se envió nada.` };
        case 'stopped':
            return { icono: 'circle-x', titulo: 'Detenida', texto: `Salieron ${cifra(c.handed_count ?? 0)} de ${cifra(c.frozen_recipients ?? 0)}; el resto no se envió.` };
        case 'draft':
            return { icono: 'file-text', titulo: 'Borrador', texto: 'Todavía no se ha enviado ni programado.' };
    }
}
